import React, { useState, useEffect } from 'react';
import { X, Clock, Calendar as CalendarIcon, AlertCircle, Repeat } from 'lucide-react';
import { format, addDays, addWeeks, addMonths, addYears, isBefore } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import { API_BASE_URL } from '../../config/api';
import type { AvailabilityType } from './WorkScheduleCalendar';
import { fetchCachedTeamMembers, type TeamMember } from '../../utils/teamMembersCache';
import { useEscapeKey } from '../../hooks/useEscapeKey';

interface WorkScheduleCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  initialStart?: Date;
  initialEnd?: Date;
  initialVetId?: string;
}

const WorkScheduleCreateModal: React.FC<WorkScheduleCreateModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  initialStart,
  initialEnd,
  initialVetId,
}) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);

  useEscapeKey(onClose, isOpen);

  // Form state
  const [selectedVetUserId, setSelectedVetUserId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endDate, setEndDate] = useState('');
  const [endTime, setEndTime] = useState('');
  const [availabilityType, setAvailabilityType] = useState<AvailabilityType>('AVAILABLE');
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  
  // Recurring state
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringFrequency, setRecurringFrequency] = useState<'daily' | 'weekly' | 'monthly'>('weekly');
  // Default to one year from today
  const [recurringEndDate, setRecurringEndDate] = useState(() => format(addYears(new Date(), 1), 'yyyy-MM-dd'));
  const [selectedWeekdays, setSelectedWeekdays] = useState<number[]>([1, 2, 3, 4, 5]); // Mon-Fri by default

  // Fetch team members (with caching)
  useEffect(() => {
    const loadTeamMembers = async () => {
      if (isOpen && user?.practice_id) {
        const token = localStorage.getItem('access_token') || localStorage.getItem('token');
        console.log('🔍 [WorkScheduleCreateModal] Fetching team members', { hasToken: !!token, practiceId: user.practice_id, initialVetId });
        const members = await fetchCachedTeamMembers(user.practice_id, token);
        console.log(`✅ [WorkScheduleCreateModal] Loaded ${members.length} team members`);
        setTeamMembers(members);
        
        // Always use initialVetId if provided, otherwise default to current user
        if (members.length > 0) {
          const vetToSelect = initialVetId || user.id;
          console.log(`👤 [WorkScheduleCreateModal] Setting vet to: ${vetToSelect} (initialVetId: ${initialVetId}, userId: ${user.id})`);
          setSelectedVetUserId(vetToSelect);
        }
      }
    };

    loadTeamMembers();
  }, [isOpen, user?.practice_id, user?.id, initialVetId]);

  // Initialize form with initial values
  useEffect(() => {
    if (isOpen && initialStart) {
      setStartDate(format(initialStart, 'yyyy-MM-dd'));
      setStartTime(format(initialStart, 'HH:mm'));
      // Default recurring end date to 1 year from start
      setRecurringEndDate(format(addYears(initialStart, 1), 'yyyy-MM-dd'));
      
      if (initialEnd) {
        setEndDate(format(initialEnd, 'yyyy-MM-dd'));
        setEndTime(format(initialEnd, 'HH:mm'));
      } else {
        // Default to 1 hour later
        const endDateTime = new Date(initialStart.getTime() + 60 * 60 * 1000);
        setEndDate(format(endDateTime, 'yyyy-MM-dd'));
        setEndTime(format(endDateTime, 'HH:mm'));
      }
    } else if (isOpen) {
      // Default to now + 1 hour
      const now = new Date();
      const later = new Date(now.getTime() + 60 * 60 * 1000);
      setStartDate(format(now, 'yyyy-MM-dd'));
      setStartTime(format(now, 'HH:mm'));
      setEndDate(format(later, 'yyyy-MM-dd'));
      setEndTime(format(later, 'HH:mm'));
      // Default recurring end date to 1 year from now
      setRecurringEndDate(format(addYears(now, 1), 'yyyy-MM-dd'));
      // Default to current user if not set
      if (!selectedVetUserId && user?.id) {
        setSelectedVetUserId(user.id);
      }
    }
  }, [isOpen, initialStart, initialEnd, user?.id]);

  const toggleWeekday = (day: number) => {
    setSelectedWeekdays(prev => 
      prev.includes(day) 
        ? prev.filter(d => d !== day)
        : [...prev, day].sort()
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // Combine date and time
      const startDateTime = new Date(`${startDate}T${startTime}`);
      const endDateTime = new Date(`${endDate}T${endTime}`);

      if (endDateTime <= startDateTime) {
        setError('End time must be after start time');
        setLoading(false);
        return;
      }

      // Extract just the time portions for recurring schedules
      const startTimeOnly = startTime; // "HH:mm" format
      const endTimeOnly = endTime; // "HH:mm" format

      if (isRecurring && recurringFrequency === 'weekly' && recurringEndDate) {
        // RFC 5545 Compliant: Create ONE event with multiple BYDAY values
        const response = await fetch(`${API_BASE_URL}/api/v1/scheduling/recurring-availability`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          },
          body: JSON.stringify({
            vet_user_id: selectedVetUserId,
            practice_id: user?.practice_id,
            days_of_week: selectedWeekdays,  // Send array of days in ONE request
            start_time: startTimeOnly,
            end_time: endTimeOnly,
            availability_type: availabilityType,
            title: title || undefined,
            effective_from: startDate,
            effective_until: recurringEndDate,
            is_active: true,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
          throw new Error(errorData.detail || 'Failed to create recurring availability');
        }

      } else if (isRecurring && recurringEndDate) {
        // For daily/monthly, generate individual blocks
        const occurrences: Array<{ start: Date; end: Date }> = [];
        const recurrenceEnd = new Date(recurringEndDate);
        let currentDate = new Date(startDateTime);
        
        while (isBefore(currentDate, recurrenceEnd) || currentDate.getTime() === recurrenceEnd.getTime()) {
          const duration = endDateTime.getTime() - startDateTime.getTime();
          
          if (recurringFrequency === 'daily') {
            const occurrenceEnd = new Date(currentDate.getTime() + duration);
            occurrences.push({ start: new Date(currentDate), end: occurrenceEnd });
            currentDate = addDays(currentDate, 1);
          } else if (recurringFrequency === 'monthly') {
            const occurrenceEnd = new Date(currentDate.getTime() + duration);
            occurrences.push({ start: new Date(currentDate), end: occurrenceEnd });
            currentDate = addMonths(currentDate, 1);
          }
          
          // Safety limit
          if (occurrences.length > 365) {
            setError('Too many occurrences (max 365). Please adjust your recurrence settings.');
            setLoading(false);
            return;
          }
        }

        // Create all occurrences
        const promises = occurrences.map(occurrence => 
          fetch(`${API_BASE_URL}/api/v1/scheduling-unix/vet-availability`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            },
            body: JSON.stringify({
              vet_user_id: selectedVetUserId,
              practice_id: user?.practice_id,
              start_at: occurrence.start.toISOString(),
              end_at: occurrence.end.toISOString(),
              availability_type: availabilityType,
              title: title || undefined,
              notes: notes || null,
            }),
          })
        );

        const results = await Promise.all(promises);
        const failed = results.filter(r => !r.ok);
        
        if (failed.length > 0) {
          throw new Error(`Failed to create ${failed.length} of ${occurrences.length} availability blocks`);
        }

      } else {
        // Single occurrence
        const response = await fetch(`${API_BASE_URL}/api/v1/scheduling-unix/vet-availability`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          },
          body: JSON.stringify({
            vet_user_id: selectedVetUserId,
            practice_id: user?.practice_id,
            start_at: startDateTime.toISOString(),
            end_at: endDateTime.toISOString(),
            availability_type: availabilityType,
            title: title || undefined,
            notes: notes || null,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Failed to create availability');
        }
      }

      // Reset form
      setStartDate('');
      setStartTime('');
      setEndDate('');
      setEndTime('');
      setAvailabilityType('AVAILABLE');
      setNotes('');
      setIsRecurring(false);
      setRecurringEndDate('');
      setSelectedWeekdays([1, 2, 3, 4, 5]);
      
      onSuccess();
    } catch (err: any) {
      console.error('Error creating availability:', err);
      setError(err.message || 'Failed to create availability');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 bg-black bg-opacity-30 transition-opacity"
      onClick={onClose}
    >
      <div
        className="fixed right-0 top-0 h-full w-full max-w-2xl bg-white shadow-2xl transform transition-transform duration-300 ease-out overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        style={{ transform: isOpen ? 'translateX(0)' : 'translateX(100%)' }}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <h2 className="text-lg font-semibold text-gray-900">Add Availability</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="px-6 py-6 space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4 flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-sm font-medium text-red-800">Error</h3>
                  <p className="text-sm text-red-700 mt-1">{error}</p>
                </div>
              </div>
            )}

            {/* Doctor Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Doctor *
              </label>
              <select
                value={selectedVetUserId}
                onChange={(e) => setSelectedVetUserId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white appearance-none cursor-pointer hover:border-gray-400 transition-colors"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                  backgroundPosition: 'right 0.5rem center',
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: '1.5em 1.5em',
                  paddingRight: '2.5rem'
                }}
                required
              >
                {teamMembers.map(member => (
                  <option key={member.id} value={member.id}>
                    {member.full_name || member.email} ({member.role})
                  </option>
                ))}
              </select>
            </div>

            {/* Availability Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Availability Type
              </label>
              <select
                value={availabilityType}
                onChange={(e) => setAvailabilityType(e.target.value as AvailabilityType)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="AVAILABLE">Available</option>
                <option value="SURGERY_BLOCK">Surgery Block</option>
                <option value="UNAVAILABLE">Unavailable</option>
                <option value="EMERGENCY_ONLY">Emergency Only</option>
              </select>
            </div>

            {/* Title (optional) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Title <span className="text-gray-400 text-xs">(optional)</span>
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g., Lunch Break, Surgery Hours"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                maxLength={255}
              />
            </div>

            {/* Start Date and Time */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Date
                </label>
                <div className="relative">
                  <CalendarIcon 
                    className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 cursor-pointer z-10" 
                    onClick={() => {
                      const input = document.getElementById('start-date-input') as HTMLInputElement;
                      input?.showPicker?.();
                    }}
                  />
                  <input
                    id="start-date-input"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Time
                </label>
                <div className="relative">
                  <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="time"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>
            </div>

            {/* End Date and Time */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  End Date
                </label>
                <div className="relative">
                  <CalendarIcon 
                    className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 cursor-pointer z-10" 
                    onClick={() => {
                      const input = document.getElementById('end-date-input') as HTMLInputElement;
                      input?.showPicker?.();
                    }}
                  />
                  <input
                    id="end-date-input"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  End Time
                </label>
                <div className="relative">
                  <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="time"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes (optional)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                maxLength={500}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                placeholder="Add any notes about this availability block..."
              />
              <p className="text-xs text-gray-500 mt-1">{notes.length}/500 characters</p>
            </div>

            {/* Recurring Schedule */}
            <div className="border-t border-gray-200 pt-6">
              <div className="flex items-center gap-3 mb-4">
                <input
                  type="checkbox"
                  id="isRecurring"
                  checked={isRecurring}
                  onChange={(e) => setIsRecurring(e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                />
                <label htmlFor="isRecurring" className="flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer">
                  <Repeat className="h-4 w-4" />
                  Repeat this availability
                </label>
              </div>

              {isRecurring && (
                <div className="space-y-4 ml-7 pl-4 border-l-2 border-blue-200">
                  {/* Frequency */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Repeat Frequency
                    </label>
                    <select
                      value={recurringFrequency}
                      onChange={(e) => setRecurringFrequency(e.target.value as 'daily' | 'weekly' | 'monthly')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly (Recommended)</option>
                      <option value="monthly">Monthly</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      {recurringFrequency === 'weekly' 
                        ? '✓ Creates reusable schedule templates'
                        : '⚠️ Creates individual blocks for each occurrence'}
                    </p>
                  </div>

                  {/* Weekday Selection (only for weekly) */}
                  {recurringFrequency === 'weekly' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Repeat On
                      </label>
                      <div className="flex gap-2 flex-wrap">
                        {[
                          { day: 0, label: 'Sun' },
                          { day: 1, label: 'Mon' },
                          { day: 2, label: 'Tue' },
                          { day: 3, label: 'Wed' },
                          { day: 4, label: 'Thu' },
                          { day: 5, label: 'Fri' },
                          { day: 6, label: 'Sat' },
                        ].map(({ day, label }) => (
                          <button
                            key={day}
                            type="button"
                            onClick={() => toggleWeekday(day)}
                            className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                              selectedWeekdays.includes(day)
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                      {selectedWeekdays.length === 0 && (
                        <p className="text-xs text-red-600 mt-1">Please select at least one day</p>
                      )}
                    </div>
                  )}

                  {/* End Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Repeat Until
                    </label>
                    <div className="relative">
                      <CalendarIcon 
                        className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 cursor-pointer z-10" 
                        onClick={() => {
                          const input = document.getElementById('recurring-end-date-input') as HTMLInputElement;
                          input?.showPicker?.();
                        }}
                      />
                      <input
                        id="recurring-end-date-input"
                        type="date"
                        value={recurringEndDate}
                        onChange={(e) => setRecurringEndDate(e.target.value)}
                        min={startDate}
                        className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required={isRecurring}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {recurringFrequency === 'weekly' 
                        ? 'Template will be active until this date'
                        : 'Individual blocks will be created until this date (max 365)'}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3 sticky bottom-0 bg-white">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-800 transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || (recurringFrequency === 'weekly' && selectedWeekdays.length === 0)}
              className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Creating...' : isRecurring ? 'Create Recurring Schedule' : 'Create Availability'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WorkScheduleCreateModal;

